import { useContext, useEffect, useRef, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import ApiHandlerContext from './ApiHandlerContext';
import HttpMethods from './HttpMethods';
import { executeHttpRequest } from './request-executor';
import {
  ApiError,
  ResponsePagination,
  ResponseState,
  UsePaginatedRequestParameters,
  UsePaginatedRequestState,
} from './types';
import useRequest from './useRequest';
import {
  completeConfigWithCancellationTokenSource,
  isErrorResponse,
  isSuccessResponse,
} from './utils';

interface RequestState<T> {
  data: T;
  errorFirstPage: ApiError;
  errorNextPage: ApiError;
  isLoadingFirstRequest: boolean;
  isLoadingNextRequest: boolean;
  pagination: ResponsePagination;
  status: number;
}

/**
 * This hook encapsulates the flow of HTTP requests, containing the response
 * data for both success and error scenarios, as well the information about if
 * the request is already finished or if it is still executing.
 *
 * Using this hook allows us to normalise our HTTP requests throughout all
 * applications.
 *
 * If used with ApiHandlerProvider and the app errors collection is passed to the
 * provider you will recieve the corresponding error if a request fails.
 *
 * When using one of the request methods, isLoadingFirstPage or isLoadingNextPage
 * value will be updated to 'true' while the request is performing. When it finishes,
 * isLoadingFirstPage or isLoadingNextPage will be set to 'false' and one of the values
 * (data or error) will be set with the corresponding value.
 *
 * @param {string} requestMethod The HTTP method to execute
 *          (e.g. GET|POST).
 * @param {string} requestUrl The resource URL schema to make a request to.
 *          (e.g. '/cards/:cardId')
 */
function usePaginatedRequest<T>(
  requestMethod: HttpMethods.GET | HttpMethods.POST,
  requestUrl: string,
  parameters?: UsePaginatedRequestParameters
): UsePaginatedRequestState<T[]> {
  const [requestState, setRequestState] = useState<RequestState<T[]>>({
    data: null,
    errorFirstPage: null,
    errorNextPage: null,
    isLoadingFirstRequest: parameters?.executeImmediately || null,
    isLoadingNextRequest: null,
    pagination: null,
    status: null,
  });

  const firstPageRequest = useRequest<T[]>(requestMethod, requestUrl);

  const requestContext = useContext(ApiHandlerContext);
  const cancellationTokenSource = useRef(axios.CancelToken.source());

  useEffect(() => {
    if (parameters?.executeImmediately === true) executeFirstPage();
  }, []);

  async function executeFirstPage(
    urlParameters?: Record<string, any>,
    requestBody?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<ResponseState<T[]>> {
    setRequestState({
      ...requestState,
      isLoadingFirstRequest: true,
    });

    const result = await firstPageRequest.execute(
      urlParameters,
      requestBody,
      config
    );

    setRequestState({
      ...requestState,
      isLoadingFirstRequest: false,
      data: result.data,
      errorFirstPage: result.error,
      errorNextPage: null,
      pagination: result.pagination,
      status: result.status,
    });

    return result;
  }

  /**
   * This function controls the flow of the requests, updating the stateful
   * values when necessary.
   *
   * Before making the request, isLoading is set to true.
   *
   * After the request is finished, data and error are extracted from the HTTP
   * response and isLoading is set to false.
   *
   * @param {Record<string, any>} requestBody Data to send with the request.
   * @param {AxiosRequestConfig} config Axios config to pass with the request.
   *
   * @return {Promise<ResponseState>} An object containing the values of data and error
   *     resulting from the request.
   */
  async function executeNextPage(
    requestBody?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<ResponseState<T[]>> {
    if (!hasNextPage()) return null;

    setRequestState({
      ...requestState,
      isLoadingNextRequest: true,
    });

    const axiosConfig = completeConfigWithCancellationTokenSource(
      {
        ...requestContext.defaultConfig,
        ...config,
      },
      cancellationTokenSource.current
    );

    const response = await executeHttpRequest<T[]>(
      requestMethod,
      getNextRequestUrl(),
      axiosConfig,
      requestBody
    );

    const hasError = isErrorResponse(response.status);

    const responseError = hasError ? response.error : null;

    let newDataValue: T[] = null;

    if (!hasError) {
      newDataValue =
        parameters?.appendData === false
          ? response?.data
          : [...requestState.data, ...response?.data];
    }

    setRequestState({
      ...requestState,
      isLoadingNextRequest: false,
      data: newDataValue,
      errorFirstPage: null,
      errorNextPage: responseError,
      pagination: response.pagination,
      status: response.status,
    });

    return {
      data: newDataValue,
      error: responseError,
      pagination: response.pagination,
      status: response.status,
      isSuccess: isSuccessResponse(response.status),
      isError: isErrorResponse(response.status),
    };
  }

  function getNextRequestUrl() {
    if (parameters?.paginationBaseUrl) {
      const separator =
        parameters.paginationBaseUrl.endsWith('/') ||
        requestState.pagination.next.startsWith('/')
          ? ''
          : '/';

      return `${parameters?.paginationBaseUrl}${separator}${requestState.pagination.next}`;
    }

    return requestState.pagination.next;
  }

  function hasNextPage() {
    return !!requestState.pagination?.next;
  }

  function cancel() {
    firstPageRequest.cancel();
    cancellationTokenSource.current.cancel('Cancelled');
  }

  return {
    /**
     * A stateful value containing the responses of all the requests made in case of
     * success.
     */
    data: requestState.data,
    /**
     * A stateful value containing if the first page HTTP request is currently executing or
     * not. The value will be null before a request is fired.
     */
    isLoadingFirstPage: requestState.isLoadingFirstRequest,
    /**
     * Executes the HTTP request to retrieve the first page of data.
     *
     * @param {Record<string, any>} urlParameters The values to replace the url params with.
     * @param {Record<string, any>} requestBody Data to send with the request.
     * @param {AxiosRequestConfig} config Axios config to pass with the request.
     */
    executeFirstPage,
    /**
     * A stateful value containing if a following page HTTP request is currently executing or
     * not. The value will be null before a request is fired.
     */
    isLoadingNextPage: requestState.isLoadingNextRequest,
    /**
     * A stateful value containing the error details of the request for the first page in
     * case of failure.
     */
    errorFirstPage: requestState.errorFirstPage,
    /**
     * A stateful value containing the error details of the request for the next page in
     * case of failure.
     */
    errorNextPage: requestState.errorNextPage,
    /**
     * Executes the HTTP request to retrieve the next page data of data.
     *
     * @param {Record<string, any>} requestBody Data to send with the request.
     * @param {AxiosRequestConfig} config Axios config to pass with the request.
     */
    executeNextPage,
    /**
     * Returns whether the request has a next page of data or not
     */
    hasNextPage: hasNextPage(),
    /**
     * Cancels the HTTP request.
     */
    cancel,
    /**
     * True if response status is 4xx or 5xx
     */
    isError: isErrorResponse(requestState.status),
    /**
     * True if response status is 2xx
     */
    isSuccess: isSuccessResponse(requestState.status),
  };
}

export default usePaginatedRequest;
